import { patientApi as http, getPatientToken } from '../../../api/http';
import { io, type Socket } from 'socket.io-client';

/** The server's statuses — the same words the doctor apps read. */
export type TeleStatus = 'SCHEDULED' | 'WAITING' | 'IN_PROGRESS' | 'ENDED' | 'COMPLETED' | 'CANCELLED';

/** THE consultation — one per appointment, shared with the doctor. */
export interface TeleConsultation {
  id: string;
  appointmentId: string;
  status: TeleStatus;
  channelName: string | null;
  consentStatus: string;
  startedAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  doctorName?: string;
  scheduledAt: string | null;
}

export interface RtcToken {
  appId: string;
  token: string;
  channelName: string;
  uid: string;
  expiresIn: number;
}

/** `/api/patient/telemedicine/consultations/*` — the patient's side of the shared session. */
export const teleApi = {
  list: () => http.get<TeleConsultation[]>('/telemedicine/consultations'),
  /** Accepts the patient's own appointment id; one that is not theirs is "not found". */
  open: (appointmentId: string) => http.post<TeleConsultation>('/telemedicine/consultations/open', { appointmentId }),
  get: (id: string) => http.get<TeleConsultation>(`/telemedicine/consultations/${id}`),
  /** Enter the waiting room; the in-app acceptance is the recorded consent. */
  wait: (id: string) => http.post<TeleConsultation>(`/telemedicine/consultations/${id}/wait`, { consent: true }),
  /** Leave — never ends the consultation; the doctor does that. */
  leave: (id: string) => http.post<TeleConsultation>(`/telemedicine/consultations/${id}/leave`, {}),
  /** Only once the doctor has started; refused before and after. */
  token: (id: string) => http.get<RtcToken>(`/telemedicine/consultations/${id}/rtc-token`),
  chat: (id: string) => http.get<{ conversationId: string }>(`/telemedicine/consultations/${id}/chat`),
};

const SOCKET_URL = (import.meta.env?.VITE_API_BASE_URL as string | undefined) || 'http://localhost:4000';

/** A socket for one consult screen; the caller disconnects it on unmount. */
export function teleSocket(): Socket | null {
  const token = getPatientToken();
  if (!token) return null;
  return io(SOCKET_URL, { auth: { token }, transports: ['websocket', 'polling'], reconnection: true });
}
